import React, { useState, useContext, useEffect, useMemo } from "react";
import {
  Search,
  Filter,
  ChevronDown,
  ChevronUp,
  MessageSquare,
  LifeBuoy,
  Shield,
  Reply,
  Send,
  X,
  User,
  AlertCircle,
  HelpCircle,
  Briefcase,
  CreditCard,
  Settings,
  Lightbulb,
  CheckCircle,
  Lock
} from "lucide-react";
import { LoginContext } from "../LoginContext";
import { MessagesContext } from "../MessagesContext";
import { useTranslation } from "react-i18next";
import { getBaseUrl } from "../config/apiConfig";
import useMessages from "../hooks/useMessages";
import { apiService } from "../backend/api";
import { io } from "socket.io-client";
import MessagesSkeleton from "./skeletons/MessagesSkeleton";

export default function ProfileMessages() {
  const { t } = useTranslation();
  const { loginInfo } = useContext(LoginContext);
  const { chatMessages, markMessageAsRead, markAllAsViewed, updateUnreadCount, setActiveLiveChat, activeLiveChat } = useContext(MessagesContext);
  const loggedInUser = loginInfo?.username;
  const isAdmin = useMemo(() => {
    const roles = loginInfo?.user?.roles || [];
    return roles.some(role => {
      const roleName = typeof role === 'string' ? role : role?.name;
      return roleName === 'admin' || roleName === 'support';
    });
  }, [loginInfo]);

  // Use React Query hook
  const {
    data: dbMessages,
    isLoading: messagesLoading,
    isError: messagesError,
    error: messagesErrorInfo,
    markAsRead,
    sendReply,
    isSendingReply,
    closeConversation,
    isClosingConversation,
    updateSupportStatus
  } = useMessages(loggedInUser, isAdmin);

  const [supportOnline, setSupportOnline] = useState(false);
  const [activeLiveRequests, setActiveLiveRequests] = useState([]);
  const [onlineTeamMembers, setOnlineTeamMembers] = useState([]);

  // Status abrufen und Socket für Live-Monitoring initialisieren
  useEffect(() => {
    if (isAdmin) {
      const checkStatus = async () => {
        try {
          const status = await apiService.checkSupportOnline();
          setSupportOnline(status.isOnline);
          setOnlineTeamMembers(status.onlineUsers || []);
        } catch (e) {}
      };
      checkStatus();

      // Socket-Verbindung für Admin-Monitoring
      const socket = io(getBaseUrl());
      socket.emit("admin-join-support");

      socket.on("live-chat-request", (data) => {
        setActiveLiveRequests(prev => {
          const existing = prev.find(r => r.conversationId === data.conversationId);
          if (existing) {
            // Update the category if it changed
            return prev.map(r => r.conversationId === data.conversationId ? { ...r, category: data.category } : r);
          }
          return [...prev, data];
        });
      });

      socket.on("live-chat-activity", (data) => {
        setActiveLiveRequests(prev => prev.map(r =>
          r.conversationId === data.conversationId
            ? { ...r, lastMessage: data.content }
            : r
        ));
      });

      return () => socket.disconnect();
    }
  }, [isAdmin]);

  const handleToggleStatus = async () => {
    const newStatus = !supportOnline;
    setSupportOnline(newStatus);
    try {
      await updateSupportStatus(newStatus);
    } catch (e) {
      setSupportOnline(!newStatus);
    }
  };

  // State für Filter
  const [searchTerm, setSearchTerm] = useState("");
  const [dateFilter, setDateFilter] = useState("all");
  const [showFilters, setShowFilters] = useState(false);
  const [expandedMessage, setExpandedMessage] = useState(null);

  // Markiere alle Nachrichten als "angesehen" beim Betreten der Seite
  useEffect(() => {
    if (loggedInUser && !isAdmin) {
      // console.log('ProfileMessages: Marking all messages as viewed for user:', loggedInUser);
      markAllAsViewed();
    }
  }, [loggedInUser, isAdmin, markAllAsViewed]);

  // Admin-spezifische States
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categoryOverview, setCategoryOverview] = useState([]);

  // Reply-Dialog States
  const [replyDialogOpen, setReplyDialogOpen] = useState(false);
  const [selectedMessageForReply, setSelectedMessageForReply] = useState(null);
  const [replyContent, setReplyContent] = useState("");

  // Close Ticket Confirmation States
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);
  const [ticketToClose, setTicketToClose] = useState(null);
  const [currentlyClosingIds, setCurrentlyClosingIds] = useState(new Set());

  // Admin-Kategorien für Help-Chat Issues
  const adminCategories = useMemo(() => [
    {
      id: "scan_issues",
      title: t("messages.scanIssues", "Scan-Probleme"),
      description: t("messages.scanIssuesDesc", "Probleme beim Hochladen und Verarbeiten von Scans"),
      icon: "🔍",
      color: "#e53e3e",
      unreadCount: 3
    },
    {
      id: "technical_issues",
      title: t("messages.technicalIssues", "Technische Probleme"),
      description: t("messages.technicalIssuesDesc", "Allgemeine technische Schwierigkeiten und Bugs"),
      icon: "⚙️",
      color: "#dd6b20",
      unreadCount: 7
    },
    {
      id: "payment_issues",
      title: t("messages.paymentIssues", "Zahlungsprobleme"),
      description: t("messages.paymentIssuesDesc", "Probleme mit Abonnements und Zahlungen"),
      icon: "💳",
      color: "#38a169",
      unreadCount: 2
    },
    {
      id: "account_issues",
      title: t("messages.accountIssues", "Account-Probleme"),
      description: t("messages.accountIssuesDesc", "Login, Registrierung und Account-Verwaltung"),
      icon: "👤",
      color: "#3182ce",
      unreadCount: 1
    },
    {
      id: "feature_requests",
      title: t("messages.featureRequests", "Feature-Anfragen"),
      description: t("messages.featureRequestsDesc", "Wünsche für neue Funktionen und Verbesserungen"),
      icon: "💡",
      color: "#805ad5",
      unreadCount: 5
    },
    {
      id: "general_support",
      title: t("messages.generalSupport", "Allgemeiner Support"),
      description: t("messages.generalSupportDesc", "Allgemeine Fragen und Hilfestellungen"),
      icon: "❓",
      color: "#718096",
      unreadCount: 4
    }
  ], [t]);

  // Demo-Nachrichten mit Kategorien (später durch echte API ersetzen)
  const demoMessages = useMemo(() => [
    // Scan Issues
    {
      id: 1,
      sender: t("messages.demoMaxMustermann", "Max Mustermann"),
      senderType: "user",
      subject: t("messages.demoScanNotProcessing", "Scan wird nicht verarbeitet"),
      content: t("messages.demoScanNotProcessingContent", "Mein hochgeladener Scan hängt seit 2 Stunden in der Verarbeitung fest. Können Sie mir helfen?"),
      date: new Date("2024-10-18T10:30:00"),
      unread: true,
      conversationId: "conv_001",
      category: "scan_issues",
      username: "max.mustermann"
    },
    {
      id: 2,
      sender: t("messages.demoAnnaSchmidt", "Anna Schmidt"),
      senderType: "user",
      subject: t("messages.demoBadScanQuality", "Schlechte Scan-Qualität"),
      content: t("messages.demoBadScanQualityContent", "Die Qualität meines 3D-Scans ist sehr schlecht. Gibt es Tipps für bessere Ergebnisse?"),
      date: new Date("2024-10-18T09:15:00"),
      unread: true,
      conversationId: "conv_002",
      category: "scan_issues",
      username: "anna.schmidt"
    },
    {
      id: 3,
      sender: t("messages.demoDrWeber", "Dr. Weber"),
      senderType: "user",
      subject: t("messages.demoFileUploadFailed", "Datei-Upload fehlgeschlagen"),
      content: t("messages.demoFileUploadFailedContent", "Beim Upload meiner STL-Datei erhalte ich immer eine Fehlermeldung. Die Datei ist 45MB groß."),
      date: new Date("2024-10-17T16:45:00"),
      unread: true,
      conversationId: "conv_003",
      category: "scan_issues",
      username: "dr.weber"
    },

    // Technical Issues
    {
      id: 4,
      sender: t("messages.demoLisaMueller", "Lisa Müller"),
      senderType: "user",
      subject: t("messages.demoPageNotLoading", "Seite lädt nicht"),
      content: t("messages.demoPageNotLoadingContent", "Die Upload-Seite lädt bei mir nicht. Ich sehe nur einen weißen Bildschirm."),
      date: new Date("2024-10-18T14:20:00"),
      unread: true,
      conversationId: "conv_004",
      category: "technical_issues",
      username: "lisa.mueller"
    },
    {
      id: 5,
      sender: t("messages.demoThomasKlein", "Thomas Klein"),
      senderType: "user",
      subject: t("messages.demoBrowserCompat", "Browser-Kompatibilität"),
      content: t("messages.demoBrowserCompatContent", "Die Anwendung funktioniert nicht in Safari. Welche Browser werden unterstützt?"),
      date: new Date("2024-10-17T11:30:00"),
      unread: true,
      conversationId: "conv_005",
      category: "technical_issues",
      username: "thomas.klein"
    },

    // Payment Issues
    {
      id: 6,
      sender: t("messages.demoMariaGarcia", "Maria Garcia"),
      senderType: "user",
      subject: t("messages.demoPaymentFailed", "Zahlung fehlgeschlagen"),
      content: t("messages.demoPaymentFailedContent", "Meine Kreditkarte wurde abgelehnt, obwohl genug Guthaben vorhanden ist. Was kann ich tun?"),
      date: new Date("2024-10-18T13:45:00"),
      unread: true,
      conversationId: "conv_006",
      category: "payment_issues",
      username: "maria.garcia"
    },
    {
      id: 7,
      sender: t("messages.demoPeterHoffmann", "Peter Hoffmann"),
      senderType: "user",
      subject: t("messages.demoSubCancellation", "Abo-Kündigung"),
      content: t("messages.demoSubCancellationContent", "Ich möchte mein Premium-Abo kündigen. Wie gehe ich vor?"),
      date: new Date("2024-10-17T09:20:00"),
      unread: true,
      conversationId: "conv_007",
      category: "payment_issues",
      username: "peter.hoffmann"
    },

    // Account Issues
    {
      id: 8,
      sender: t("messages.demoSarahJohnson", "Sarah Johnson"),
      senderType: "user",
      subject: t("messages.demoForgotPassword", "Passwort vergessen"),
      content: t("messages.demoForgotPasswordContent", "Ich kann mich nicht mehr einloggen und habe mein Passwort vergessen. Können Sie mir helfen?"),
      date: new Date("2024-10-17T15:10:00"),
      unread: true,
      conversationId: "conv_008",
      category: "account_issues",
      username: "sarah.johnson"
    },

    // Feature Requests
    {
      id: 9,
      sender: t("messages.demoDrSchneider", "Dr. Schneider"),
      senderType: "user",
      subject: t("messages.demoDicomExport", "Export-Funktion für DICOM"),
      content: t("messages.demoDicomExportContent", "Wäre es möglich, eine Export-Funktion für DICOM-Dateien hinzuzufügen?"),
      date: new Date("2024-10-18T08:30:00"),
      unread: true,
      conversationId: "conv_009",
      category: "feature_requests",
      username: "dr.schneider"
    },

    // General Support
    {
      id: 10,
      sender: t("messages.demoMichaelBrown", "Michael Brown"),
      senderType: "user",
      subject: t("messages.demoGeneralUsage", "Allgemeine Frage zur Nutzung"),
      content: t("messages.demoGeneralUsageContent", "Ich bin neu hier und hätte gerne eine Einführung in die wichtigsten Funktionen."),
      date: new Date("2024-10-17T12:00:00"),
      unread: true,
      conversationId: "conv_010",
      category: "general_support",
      username: "michael.brown"
    }
  ], [t]);

  // Kombiniere DB-Nachrichten mit Demo-Daten falls nötig
  const messages = useMemo(() => {
    if (messagesError) {
      console.error("❌ ProfileMessages: Error fetching messages:", messagesErrorInfo);
      return [];
    }
    if (!dbMessages) return [];

    // console.log(`📊 ProfileMessages: Processing ${dbMessages.length} messages (isAdmin: ${isAdmin})`);
    if (dbMessages.length > 0) return dbMessages;

    // Fallback zu Demo-Daten falls keine echten Nachrichten vorhanden und nicht Admin
    if (!isAdmin && loggedInUser) {
      return [{
        id: 999,
        sender: t("messages.supportTeam", "Support Team"),
        senderType: "support",
        subject: t("messages.welcomeSubject", "Willkommen bei 3D-Medico!"),
        content: t("messages.welcomeContent", "Willkommen bei 3D-Medico! Falls Sie Fragen haben, können Sie uns jederzeit über dieses Nachrichtensystem kontaktieren."),
        date: new Date(),
        unread: true,
        conversationId: "conv_welcome",
        category: "general_support",
        username: loggedInUser,
        recipientUsername: loggedInUser,
        isFromDatabase: false
      }];
    }
    return [];
  }, [dbMessages, isAdmin, loggedInUser, messagesError, messagesErrorInfo, t]);

  // Berechne Category Overview für Admin
  useEffect(() => {
    if (isAdmin) {
      const categoryStats = adminCategories.map(category => ({
        ...category,
        unreadCount: messages.filter(msg =>
          ((msg.category === category.id) || (category.id === "general_support" && (!msg.category || !adminCategories.some(cat => cat.id === msg.category)))) &&
          msg.unread &&
          (msg.recipientUsername === "admin" || msg.recipientType === "admin" || (loggedInUser && msg.recipientUsername === loggedInUser))
        ).length || 0,
        totalCount: messages.filter(msg =>
          (msg.category === category.id) || (category.id === "general_support" && (!msg.category || !adminCategories.some(cat => cat.id === msg.category)))
        ).length || 0
      }));
      setCategoryOverview(categoryStats);
    }
  }, [isAdmin, messages, adminCategories, loggedInUser]);

  // Gruppiere Nachrichten zu Konversationen
  const conversations = useMemo(() => {
    const groups = {};

    messages.forEach(msg => {
      const convId = msg.conversationId || `standalone_${msg.id}`;
      if (!groups[convId]) {
        groups[convId] = {
          id: convId,
          messages: [],
          latestDate: new Date(0),
          unreadCount: 0,
          status: 'open',
          category: msg.category,
          subject: msg.subject,
          username: msg.username || msg.senderUsername,
          recipientUsername: msg.recipientUsername,
          senderName: msg.sender,
          senderType: msg.senderType
        };
      }

      groups[convId].messages.push(msg);

      const msgDate = new Date(msg.date);
      if (msgDate > groups[convId].latestDate) {
        groups[convId].latestDate = msgDate;
        groups[convId].latestMessage = msg;
        // Aktualisiere Metadaten vom neuesten Stand
        groups[convId].status = msg.status || groups[convId].status;
        if (msg.subject && !groups[convId].subject?.startsWith("Re:")) {
           groups[convId].subject = msg.subject;
        }
      }

      if (msg.unread) {
        groups[convId].unreadCount++;
      }
      if (msg.status === 'closed') {
        groups[convId].status = 'closed';
      }
    });

    return Object.values(groups).map(conv => {
      // Sortiere Nachrichten innerhalb der Konversation chronologisch (alt zu neu)
      conv.messages.sort((a, b) => new Date(a.date) - new Date(b.date));
      return conv;
    });
  }, [messages]);

  // Filtere Konversationen basierend auf Suchbegriff, Datum und Kategorie
  const filteredConversations = conversations.filter(conv => {
    const latestMsg = conv.latestMessage;

    const matchesSearch = searchTerm === "" ||
      conv.subject?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      conv.messages.some(m => m.content.toLowerCase().includes(searchTerm.toLowerCase())) ||
      conv.username?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesDate = dateFilter === "all" || (() => {
      const convDate = conv.latestDate;
      const now = new Date();

      switch (dateFilter) {
        case "today":
          return convDate.toDateString() === now.toDateString();
        case "week":
          const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          return convDate >= weekAgo;
        case "month":
          const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          return convDate >= monthAgo;
        default:
          return true;
      }
    })();

    const matchesCategory = !selectedCategory ||
      (conv.category === selectedCategory) ||
      (selectedCategory === "general_support" && (!conv.category || !adminCategories.some(cat => cat.id === conv.category)));

    return matchesSearch && matchesDate && matchesCategory;
  });

  // Sortiere Konversationen: Ungelesene zuerst (nach konv status), dann nach Datum der letzten Nachricht
  const sortedConversations = filteredConversations.sort((a, b) => {
    const aUnread = a.unreadCount > 0;
    const bUnread = b.unreadCount > 0;
    if (aUnread && !bUnread) return -1;
    if (!aUnread && bUnread) return 1;
    return b.latestDate - a.latestDate;
  });

  // Hilfsfunktionen
  const getSenderIcon = (senderType) => {
    switch (senderType) {
      case "support":
        return <LifeBuoy size={18} />;
      case "admin":
        return <Shield size={18} />;
      case "system":
        return <MessageSquare size={18} />;
      default:
        return <MessageSquare size={18} />;
    }
  };

  const getSenderColorClass = (senderType) => {
    switch (senderType) {
      case "support":
        return "text-[#0f7699] dark:text-[#4fc3f7] bg-blue-50 dark:bg-blue-900/20";
      case "admin":
        return "text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20";
      case "system":
        return "text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20";
      default:
        return "text-slate-600 dark:text-slate-400 bg-slate-50 dark:bg-slate-700";
    }
  };

  const getSenderAvatarColor = (senderType) => {
    switch (senderType) {
      case "support":
        return "bg-[#0f7699]";
      case "admin":
        return "bg-red-600";
      case "system":
        return "bg-amber-600";
      default:
        return "bg-slate-400";
    }
  };

  const formatDate = (date) => {
    const messageDate = new Date(date);
    const now = new Date();
    const diffTime = Math.abs(now - messageDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 1) {
      return t("messages.today", "Heute");
    } else if (diffDays === 2) {
      return t("messages.yesterday", "Gestern");
    } else if (diffDays <= 7) {
      return t("messages.daysAgo", "vor {{days}} Tagen", { days: diffDays - 1 });
    } else {
      return messageDate.toLocaleDateString('de-DE');
    }
  };

  const handleMessageClick = async (convId) => {
    const isExpanding = expandedMessage !== convId;
    setExpandedMessage(isExpanding ? convId : null);

    // Markiere alle ungelesenen Nachrichten in der Konversation als gelesen
    if (isExpanding) {
      const conv = conversations.find(c => c.id === convId);
      if (conv && conv.unreadCount > 0) {
        conv.messages.forEach(msg => {
          if (msg.unread && msg.isFromDatabase) {
            markAsRead(msg.id);
          }
        });
      }
    }
  };

  // Berechne unread count nur für Nachrichten, die der aktuelle User empfangen hat oder für Admin allgemein
  const unreadCount = messages.filter(msg =>
    msg.unread && (
      msg.recipientUsername === loggedInUser ||
      (isAdmin && (msg.recipientUsername === "admin" || msg.recipientType === "admin")) ||
      (!msg.recipientUsername && !isAdmin && msg.username !== loggedInUser) // Fallback für Demo-Nachrichten für User
    )
  ).length;

  // Debug: Log unread count
  // console.log('ProfileMessages: unreadCount =', unreadCount);
  // console.log('ProfileMessages: messages =', messages.length);
  // console.log('ProfileMessages: unread messages =', messages.filter(msg => msg.unread));
  // Admin-spezifische Funktionen
  const handleCategoryClick = (categoryId) => {
    setSelectedCategory(categoryId);
    setSearchTerm(""); // Reset search when selecting category
  };

  const handleBackToOverview = () => {
    setSelectedCategory(null);
    setSearchTerm("");
  };

  const getCategoryTitle = (categoryId) => {
    const category = adminCategories.find(cat => cat.id === categoryId);
    return category ? category.title : t("messages.unknownCategory", "Unbekannte Kategorie");
  };

  const getCategoryIcon = (categoryId) => {
    const category = adminCategories.find(cat => cat.id === categoryId);
    return category ? category.icon : "📝";
  };

  // Reply-Handler-Funktionen
  const handleOpenReplyDialog = (message) => {
    setSelectedMessageForReply(message);
    setReplyDialogOpen(true);
  };

  const handleCloseReplyDialog = () => {
    setReplyDialogOpen(false);
    setSelectedMessageForReply(null);
    setReplyContent("");
  };

  const handleSendReply = () => {
    if (!replyContent.trim() || !selectedMessageForReply) return;

    const currentUser = loginInfo?.username || "user";

    sendReply({
      senderUsername: currentUser,
      senderType: isAdmin ? "admin" : "user",
      senderName: isAdmin ? t("messages.supportTeam", "Support Team") : (loginInfo?.user?.displayName || currentUser),
      recipientUsername: selectedMessageForReply.username,
      recipientType: isAdmin ? "user" : (selectedMessageForReply.senderType || "admin"),
      subject: selectedMessageForReply.subject.startsWith("Re:") ? selectedMessageForReply.subject : `Re: ${selectedMessageForReply.subject}`,
      content: replyContent,
      category: selectedMessageForReply.category,
      conversationId: selectedMessageForReply.conversationId,
      replyTo: selectedMessageForReply.id
    }, {
      onSuccess: () => {
        // Aktualisiere unread count für den Empfänger (optional, backend handles this)
        setTimeout(() => {
          updateUnreadCount();
        }, 1000);
        handleCloseReplyDialog();
      },
      onError: (error) => {
        console.error("Error sending reply:", error);
      }
    });
  };

  if (messagesLoading) return <MessagesSkeleton />;

  // Admin Category Overview Component
  const AdminCategoryOverview = () => (
    <div className="w-full space-y-8">
      <div className="bg-white dark:bg-slate-800 rounded-[2rem] sm:rounded-[2.5rem] shadow-sm border border-slate-200 dark:border-slate-700 p-5 sm:p-8 md:p-10">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 sm:gap-6">
          <div className="space-y-1">
            <h2 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight">
              📋 {t("messages.supportCategoriesOverview", "Support-Kategorien Übersicht")}
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 font-medium italic">
              {t("messages.allRequestsSortedByCategory", "Alle Help-Chat Anfragen nach Kategorien sortiert")}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-center gap-4">
             <div className="flex items-center gap-4 px-4 sm:px-6 py-2 sm:py-3 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800/30 rounded-2xl sm:rounded-3xl shadow-sm">
                 <div className="flex flex-col">
                    <span className="text-[9px] font-black text-red-400 uppercase tracking-widest leading-none mb-1">Live Chat Status</span>
                    <span className={`text-[10px] font-black uppercase tracking-tight ${supportOnline ? "text-emerald-600" : "text-amber-600"}`}>
                      {supportOnline ? "ONLINE" : "OFFLINE"}
                    </span>
                 </div>
                 <button
                  onClick={handleToggleStatus}
                  className={`relative w-10 h-5 rounded-full transition-all duration-300 ${supportOnline ? "bg-emerald-500" : "bg-slate-300"}`}
                 >
                   <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-all duration-300 ${supportOnline ? "left-5.5" : "left-0.5"}`} />
                 </button>
              </div>

            <div className="px-4 py-2 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-xl text-xs font-black uppercase tracking-widest border border-red-100 dark:border-red-800/30 shadow-sm shadow-red-900/5">
              {unreadCount} {t("messages.totalUnread", "ungelesen")}
            </div>
          </div>
        </div>

        {/* Live Now Section */}
        {activeLiveRequests.length > 0 && (
          <div className="mt-8 pt-8 border-t border-slate-100 dark:border-slate-700">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></div>
              <h3 className="text-xs font-black uppercase tracking-widest text-slate-400">
                {t("messages.liveNow", "Live Jetzt")} ({activeLiveRequests.length})
              </h3>
            </div>
            <div className="flex flex-wrap gap-3">
              {activeLiveRequests.map((req) => (
                <div
                  key={req.conversationId}
                  className="flex items-center gap-4 px-5 py-3 bg-slate-900 text-white rounded-2xl shadow-xl shadow-slate-900/20 animate-in zoom-in-95 duration-300"
                >
                  <div className="flex flex-col">
                    <span className="text-[9px] font-bold text-white/50 uppercase tracking-widest leading-none mb-0.5">User Chat</span>
                    <span className="text-xs font-black">{req.username}</span>
                    {req.lastMessage && (
                      <span className="text-[10px] text-white/70 italic line-clamp-1 max-w-[150px] mt-1">
                        "{req.lastMessage}"
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 border-l border-white/10 pl-4">
                    <button
                      onClick={() => {
                        setActiveLiveChat({
                          roomId: req.conversationId,
                          username: req.username,
                          category: req.category
                        });
                        // Close this request from the dashboard (admin has picked it up)
                        setActiveLiveRequests(prev => prev.filter(r => r.conversationId !== req.conversationId));
                      }}
                      className="px-4 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-[10px] font-black uppercase tracking-widest rounded-lg transition-colors"
                    >
                      {t("messages.joinChat", "Beitreten")}
                    </button>
                    <button
                      onClick={() => setActiveLiveRequests(prev => prev.filter(r => r.conversationId !== req.conversationId))}
                      className="p-1.5 hover:bg-white/10 rounded-lg text-white/40 hover:text-white transition-colors"
                    >
                      <X size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {categoryOverview.map((category) => (
          <div
            key={category.id}
            onClick={() => handleCategoryClick(category.id)}
            className={`
              group cursor-pointer p-5 sm:p-8 rounded-[1.5rem] sm:rounded-[2rem] bg-white dark:bg-slate-800 border-2 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] shadow-sm hover:shadow-xl
              ${category.unreadCount > 0 ? "border-[#0f7699] dark:border-[#4fc3f7] shadow-blue-900/5" : "border-slate-100 dark:border-slate-700 hover:border-slate-200 dark:hover:border-slate-600"}
            `}
          >
            <div className="flex items-start justify-between mb-6">
              <div className="text-4xl group-hover:scale-110 transition-transform duration-300">
                {category.icon}
              </div>
              {category.unreadCount > 0 && (
                <div className="px-3 py-1 bg-[#0f7699] text-white text-[10px] font-black uppercase tracking-widest rounded-full animate-pulse">
                  {category.unreadCount} {t("messages.new", "neu")}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <h3 className="text-lg font-black text-slate-800 dark:text-white tracking-tight transition-colors group-hover:text-[#0f7699] dark:group-hover:text-[#4fc3f7]">
                {category.title}
              </h3>
              <p className="text-xs text-slate-400 dark:text-slate-500 font-medium leading-relaxed">
                {category.description}
              </p>
            </div>

            <div className="mt-8 pt-6 border-t border-slate-50 dark:border-slate-700 flex items-center justify-between">
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                {category.totalCount} {t("messages.total", "gesamt")}
              </span>
              <div className="w-8 h-8 rounded-full border border-slate-100 flex items-center justify-center text-slate-300 group-hover:text-[#0f7699] group-hover:border-[#0f7699] transition-all">
                <Send size={12} className="rotate-45" />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="w-full space-y-8 pb-20">
      {/* Compact Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3 sm:gap-4">
          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#0f7699]/10 dark:bg-[#4fc3f7]/10 rounded-xl sm:rounded-2xl flex items-center justify-center text-[#0f7699] dark:text-[#4fc3f7]">
            <MessageSquare size={20} className="sm:hidden" /><MessageSquare size={24} className="hidden sm:block" />
          </div>
          <div>
            <h1 className="text-lg sm:text-2xl font-black text-[#0f7699] dark:text-[#4fc3f7] tracking-tight">
              {isAdmin ? t("messages.supportDashboard", "Support-Dashboard") : t("messages.myMessages", "Meine Nachrichten")}
            </h1>
            <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 font-medium mt-0.5 hidden sm:block">
              {isAdmin
                ? t("messages.manageAllSupportRequests", "Verwalten Sie alle Support-Anfragen nach Kategorien")
                : t("messages.allYourConversations", "Alle Ihre Konversationen aus dem Help-Chat")}
            </p>
          </div>
        </div>

        {isAdmin && (
          <div className="flex items-center gap-3 bg-white dark:bg-slate-800 p-2 rounded-xl shadow-sm border border-slate-100 dark:border-slate-700 group relative">
            <div className="flex -space-x-2">
              {onlineTeamMembers.length > 0 ? (
                onlineTeamMembers.slice(0, 5).map((member, i) => (
                  <div
                    key={member.username}
                    className="w-8 h-8 rounded-full border-2 border-white bg-slate-900 flex items-center justify-center overflow-hidden"
                    title={member.displayName}
                  >
                    <span className="text-[10px] font-bold text-white uppercase">
                      {member.displayName.charAt(0)}
                    </span>
                  </div>
                ))
              ) : (
                <div className="w-8 h-8 rounded-full border-2 border-white bg-slate-200 flex items-center justify-center overflow-hidden">
                  <User size={16} className="text-slate-400" />
                </div>
              )}
              {onlineTeamMembers.length > 5 && (
                <div className="w-8 h-8 rounded-full border-2 border-white bg-slate-100 flex items-center justify-center overflow-hidden text-[10px] font-bold text-slate-400">
                  +{onlineTeamMembers.length - 5}
                </div>
              )}
            </div>
            <span className="text-sm font-medium text-slate-600 dark:text-slate-300">
              {onlineTeamMembers.length} {t("messages.teamMembersOnline", "Teammitglieder online")}
            </span>
            <div className={`w-2 h-2 rounded-full ${onlineTeamMembers.length > 0 ? 'bg-green-500 animate-pulse' : 'bg-slate-300'}`}></div>

            {/* Tooltip on hover */}
            {onlineTeamMembers.length > 0 && (
              <div className="absolute top-full left-0 mt-2 p-3 bg-slate-900 text-white text-[10px] rounded-xl shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 min-w-[150px]">
                <p className="font-black uppercase tracking-widest mb-2 text-white/50 border-b border-white/10 pb-1">
                  {t("messages.onlineStaff", "Online Mitarbeiter")}
                </p>
                <div className="space-y-1.5">
                  {onlineTeamMembers.map(m => (
                    <div key={m.username} className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-emerald-400"></div>
                      <span className="font-bold">{m.displayName}</span>
                      <span className="text-white/40 text-[8px] uppercase">{m.role}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Error Alert */}
      {messagesError && (
        <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 dark:border-red-400 p-4 rounded-r-lg flex items-start gap-3 shadow-sm mb-8">
          <AlertCircle className="text-red-500 dark:text-red-400 shrink-0 mt-0.5" size={20} />
          <div>
            <h3 className="text-red-800 dark:text-red-300 font-bold text-sm">
              {t("messages.loadingError", "Fehler beim Laden")}
            </h3>
            <p className="text-red-700 dark:text-red-400 text-sm mt-1">
              {t("messages.errorFetchingDesc", "Die Nachrichten konnten nicht vom Server geladen werden. Bitte prüfen Sie Ihre Verbindung oder versuchen Sie es später erneut.")}
            </p>
            {messagesErrorInfo?.message && (
              <p className="text-red-600 dark:text-red-400/70 text-xs mt-2 font-mono">
                {messagesErrorInfo.message}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Admin Category Overview or Message List */}
      {isAdmin && !selectedCategory ? (
        <AdminCategoryOverview />
      ) : (
        <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
          {/* List Header Card */}
          <div className="bg-white dark:bg-slate-800 rounded-[2.5rem] shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden mb-8">
            <div className="p-5 sm:p-8 md:p-10">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-8">
                <div className="space-y-1">
                  <div className="flex items-center gap-3">
                    {isAdmin && selectedCategory && (
                      <button
                        onClick={handleBackToOverview}
                        className="w-10 h-10 flex items-center justify-center rounded-xl bg-slate-50 dark:bg-slate-700 text-slate-400 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-600 hover:text-[#0f7699] dark:hover:text-[#4fc3f7] transition-all"
                      >
                        <ChevronUp className="-rotate-90" size={20} />
                      </button>
                    )}
                    <h2 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight">
                      {isAdmin && selectedCategory ? (
                        <span className="flex items-center gap-3">
                          <span className="text-3xl">{getCategoryIcon(selectedCategory)}</span>
                          {getCategoryTitle(selectedCategory)}
                        </span>
                      ) : (
                        t("messages.messagesTitle", "Nachrichten")
                      )}
                    </h2>
                  </div>
                  <p className="text-sm text-slate-500 dark:text-slate-400 font-medium italic">
                    {isAdmin && selectedCategory
                      ? `${t("messages.allMessagesInCategory", "Alle Nachrichten in der Kategorie")} "${getCategoryTitle(selectedCategory)}"`
                      : isAdmin
                        ? t("messages.allHelpChatConversations", "Alle Help-Chat Konversationen")
                        : t("messages.allYourConversations", "Alle Ihre Konversationen aus dem Help-Chat")
                    }
                  </p>
                </div>

                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className={`
                      w-12 h-12 flex items-center justify-center rounded-2xl border transition-all
                      ${showFilters ? "bg-[#0f7699] text-white border-transparent" : "bg-white dark:bg-slate-700 text-slate-400 dark:text-slate-300 border-slate-100 dark:border-slate-600 hover:border-slate-300 dark:hover:border-slate-500"}
                    `}
                  >
                    <Filter size={20} />
                  </button>
                </div>
              </div>

              {/* Search Bar */}
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-6 flex items-center pointer-events-none text-slate-400 group-focus-within:text-[#0f7699] transition-colors">
                  <Search size={20} />
                </div>
                <input
                  type="text"
                  placeholder={t("messages.searchMessages", "Nachrichten durchsuchen...")}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-14 pr-6 h-12 sm:h-16 bg-slate-50 dark:bg-slate-700 border-2 border-transparent focus:border-[#0f7699] dark:focus:border-[#4fc3f7] focus:bg-white dark:focus:bg-slate-600 rounded-[1.25rem] text-slate-700 dark:text-white font-bold placeholder:text-slate-400 dark:placeholder:text-slate-500 transition-all outline-none"
                />
              </div>

              {/* Advanced Filters Collapse */}
              <div className={`overflow-hidden transition-all duration-300 ${showFilters ? "max-h-40 opacity-100 mt-6" : "max-h-0 opacity-0"}`}>
                <div className="p-6 bg-slate-50 dark:bg-slate-700/50 rounded-2xl border border-slate-100 dark:border-slate-600 flex flex-wrap gap-4">
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black underline uppercase tracking-widest text-[#0f7699] opacity-70 ml-1">
                      {t("messages.timePeriod", "Zeitraum")}
                    </label>
                    <select
                      value={dateFilter}
                      onChange={(e) => setDateFilter(e.target.value)}
                      className="h-12 px-4 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl text-xs font-bold text-slate-600 dark:text-slate-300 outline-none focus:border-[#0f7699] dark:focus:border-[#4fc3f7]"
                    >
                      <option value="all">{t("messages.allMessages", "Alle Nachrichten")}</option>
                      <option value="today">{t("messages.today", "Heute")}</option>
                      <option value="week">{t("messages.lastWeek", "Letzte Woche")}</option>
                      <option value="month">{t("messages.lastMonth", "Letzter Monat")}</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Messages List */}
          {sortedConversations.length === 0 ? (
            <div className="bg-white dark:bg-slate-800 rounded-[2rem] sm:rounded-[2.5rem] border border-slate-100 dark:border-slate-700 p-6 sm:p-10 md:p-20 text-center space-y-4">
              <div className="w-20 h-20 bg-slate-50 dark:bg-slate-700 rounded-3xl flex items-center justify-center text-slate-300 dark:text-slate-500 mx-auto">
                <MessageSquare size={40} />
              </div>
              <div>
                <h3 className="text-xl font-black text-slate-900 dark:text-white">{t("messages.noMessagesFound", "Keine Nachrichten gefunden")}</h3>
                <p className="text-sm text-slate-400 font-medium">
                  {searchTerm || dateFilter !== "all"
                    ? t("messages.tryDifferentSearch", "Versuchen Sie andere Suchbegriffe oder Filter")
                    : t("messages.noMessagesYet", "Sie haben noch keine Nachrichten erhalten")
                  }
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {sortedConversations.map((conversation) => {
                const isExpanded = expandedMessage === conversation.id;
                const latestMsg = conversation.latestMessage;
                const unread = conversation.unreadCount > 0;

                return (
                  <div
                    key={conversation.id}
                    className={`
                      relative bg-white dark:bg-slate-800 rounded-[2rem] border-2 transition-all duration-300 overflow-hidden shadow-sm hover:shadow-md
                      ${unread ? "border-[#0f7699] dark:border-[#4fc3f7] shadow-blue-900/5" : "border-slate-100 dark:border-slate-700"}
                      ${isExpanded ? "ring-4 ring-[#0f7699]/5 dark:ring-[#4fc3f7]/5" : ""}
                    `}
                  >
                    {/* Read/Unread Indicator Line */}
                    {unread && conversation.status !== 'closed' && (
                      <div className="absolute top-0 bottom-0 left-0 w-1.5 bg-[#0f7699] shadow-lg"></div>
                    )}
                    {conversation.status === 'closed' && (
                      <div className="absolute top-0 bottom-0 left-0 w-1.5 bg-slate-300"></div>
                    )}

                    <div
                      className="p-4 sm:p-6 md:p-8 cursor-pointer group"
                      onClick={() => handleMessageClick(conversation.id)}
                    >
                      <div className="flex items-start gap-3 sm:gap-6">
                        {/* Avatar */}
                        <div className="relative shrink-0">
                          <div className={`w-10 h-10 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl flex items-center justify-center text-white shadow-lg ${getSenderAvatarColor(latestMsg.senderType)}`}>
                            {getSenderIcon(latestMsg.senderType)}
                          </div>
                          {unread && (
                            <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full border-2 border-white animate-bounce shadow-sm"></div>
                          )}
                        </div>

                        {/* Content Preview */}
                        <div className="flex-1 min-w-0">
                          <div className="flex justify-between items-center mb-1">
                            <span className={`text-[10px] font-black uppercase tracking-widest ${unread ? "text-[#0f7699]" : "text-slate-400"}`}>
                              {latestMsg.sender}
                              {isAdmin && (latestMsg.senderType === "admin" || latestMsg.senderType === "support") && (
                                <span className="text-[#0f7699] ml-1">
                                  → {t("messages.to", "An")}: {latestMsg.recipientUsername || "User"}
                                </span>
                              )}
                            </span>
                            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                              {formatDate(conversation.latestDate)}
                            </span>
                          </div>

                          <h4 className={`text-lg font-black tracking-tight mb-1 truncate ${unread ? "text-slate-900 dark:text-white" : "text-slate-600 dark:text-slate-300"}`}>
                            {conversation.subject}
                          </h4>

                          <p className={`text-sm font-medium line-clamp-1 ${isExpanded ? "hidden" : "block"} ${unread ? "text-slate-600 dark:text-slate-300 font-bold" : "text-slate-400 dark:text-slate-500"}`}>
                            {latestMsg.content}
                          </p>
                        </div>

                        {/* Expand Icon */}
                        <div className="shrink-0 self-center">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-slate-300 dark:text-slate-500 border border-slate-50 dark:border-slate-700 group-hover:border-slate-200 dark:group-hover:border-slate-600 transition-all ${isExpanded ? "rotate-180 text-[#0f7699] dark:text-[#4fc3f7] bg-blue-50 dark:bg-blue-900/20 border-transparent" : ""}`}>
                            <ChevronDown size={20} />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Expanded Content: Thread History */}
                    {isExpanded && (
                      <div className="px-4 sm:px-8 pb-6 sm:pb-8 pt-2 animate-in slide-in-from-top-4 duration-300">
                        <div className="space-y-6">
                          {/* Chronological Message Flow */}
                          <div className="space-y-4">
                            {conversation.messages.map((msg, index) => (
                              <div key={msg.id} className={`flex ${msg.senderType === 'user' ? 'justify-start' : 'justify-end'}`}>
                                <div className={`
                                  max-w-[95%] sm:max-w-[85%] p-4 sm:p-6 rounded-2xl sm:rounded-3xl border shadow-sm relative
                                  ${msg.senderType === 'user'
                                    ? 'bg-white dark:bg-slate-700 border-slate-100 dark:border-slate-600 rounded-tl-none'
                                    : 'bg-[#0f7699]/5 dark:bg-[#4fc3f7]/10 border-[#0f7699]/10 dark:border-[#4fc3f7]/10 rounded-tr-none'}
                                `}>
                                  <div className="flex justify-between items-center mb-2 gap-4">
                                    <span className="text-[10px] font-black uppercase tracking-widest text-[#0f7699]">
                                      {msg.sender}
                                    </span>
                                    <span className="text-[10px] font-bold text-slate-400">
                                      {formatDate(msg.date)} {new Date(msg.date).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                    </span>
                                  </div>
                                  <p className="text-sm text-slate-700 dark:text-slate-200 font-medium leading-relaxed whitespace-pre-wrap">
                                    {msg.content}
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>

                          {/* Metadata Badges */}
                          <div className="flex flex-wrap gap-3 pt-6 border-t border-slate-200/50 dark:border-slate-700">
                            <div className="px-4 py-2 bg-white dark:bg-slate-700 rounded-xl border border-slate-200 dark:border-slate-600 text-[10px] font-black text-slate-400 uppercase tracking-widest">
                              ID: #{conversation.id}
                            </div>
                            {isAdmin && (
                              <div className="px-4 py-2 bg-[#0f7699]/5 text-[#0f7699] rounded-xl border border-[#0f7699]/10 text-[10px] font-black uppercase tracking-widest">
                                {conversation.username ? `${t("messages.from", "Von")}: ${conversation.username}` : ""}
                              </div>
                            )}
                            {isAdmin && conversation.category && (
                                <div className="px-4 py-2 bg-slate-900 text-white rounded-xl text-[10px] font-black uppercase tracking-widest shadow-lg shadow-black/10">
                                  {getCategoryTitle(conversation.category)}
                                </div>
                            )}
                            {conversation.status === 'closed' && (
                              <div className="px-4 py-2 bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 rounded-xl border border-slate-200 dark:border-slate-600 text-[10px] font-black uppercase tracking-widest flex items-center gap-2">
                                <Lock size={10} />
                                {t("messages.ticketClosed", "Ticket Geschlossen")}
                              </div>
                            )}
                          </div>

                          {/* Action Buttons */}
                          <div className="flex flex-wrap gap-4 pt-6 border-t border-slate-200/50 dark:border-slate-700">
                            {/* Reply Button */}
                            {conversation.status !== 'closed' && !currentlyClosingIds.has(conversation.id) && (
                              <button
                                onClick={() => handleOpenReplyDialog(latestMsg)}
                                disabled={isClosingConversation}
                                className={`inline-flex items-center gap-2 sm:gap-3 px-4 sm:px-8 h-12 sm:h-14 bg-[#0f7699] text-white rounded-xl sm:rounded-2xl font-bold text-xs sm:text-sm tracking-widest shadow-xl shadow-blue-900/10 hover:bg-[#0d6482] transition-all hover:scale-[1.02] active:scale-[0.98] ${isClosingConversation ? "opacity-50 cursor-not-allowed" : ""}`}
                              >
                                <Reply size={18} />
                                <span>{t("messages.reply", "Antworten")}</span>
                              </button>
                            )}

                            {/* Close Ticket Button */}
                            {conversation.status !== 'closed' && !currentlyClosingIds.has(conversation.id) && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setTicketToClose(conversation.id);
                                  setConfirmCloseOpen(true);
                                }}
                                disabled={isClosingConversation}
                                className="inline-flex items-center gap-2 sm:gap-3 px-4 sm:px-8 h-12 sm:h-14 bg-white dark:bg-slate-700 text-slate-500 dark:text-slate-300 border-2 border-slate-100 dark:border-slate-600 rounded-xl sm:rounded-2xl font-bold text-xs sm:text-sm tracking-widest hover:bg-slate-50 dark:hover:bg-slate-600 hover:border-slate-200 dark:hover:border-slate-500 transition-all hover:scale-[1.02] active:scale-[0.98]"
                              >
                                {isClosingConversation ? (
                                  <div className="w-5 h-5 border-2 border-slate-300 border-t-slate-500 rounded-full animate-spin"></div>
                                ) : (
                                  <CheckCircle size={18} />
                                )}
                                <span>{t("messages.closeTicket", "Ticket schließen")}</span>
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Reply Dialog / Modal */}
      {replyDialogOpen && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-300"
            onClick={handleCloseReplyDialog}
          ></div>
          <div className="relative w-full max-w-2xl bg-white dark:bg-slate-800 rounded-2xl sm:rounded-[2.5rem] shadow-2xl border border-white/20 dark:border-slate-700 overflow-hidden animate-in zoom-in-95 duration-300 max-h-[95vh] flex flex-col">
            {/* Modal Header */}
            <div className="bg-[#0f7699] p-4 sm:p-6 md:p-8 text-white relative shrink-0">
              <div className="space-y-1 pr-12">
                <h3 className="text-lg sm:text-2xl font-black tracking-tight">
                  {t("messages.writeReply", "Antwort schreiben")}
                </h3>
                {selectedMessageForReply && (
                  <p className="text-xs font-bold uppercase tracking-widest text-white/60">
                    {t("messages.replyTo", "Antwort auf")}: {selectedMessageForReply.subject}
                  </p>
                )}
              </div>
              <button
                onClick={handleCloseReplyDialog}
                className="absolute top-3 right-3 sm:top-6 sm:right-6 md:top-8 md:right-8 w-10 h-10 flex items-center justify-center rounded-xl bg-white/10 hover:bg-white/20 transition-all"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-4 sm:p-6 md:p-10 space-y-4 sm:space-y-6 md:space-y-8 overflow-y-auto">
              {/* Original Message Quote */}
              {selectedMessageForReply && (
                <div className="p-4 sm:p-6 bg-slate-50 dark:bg-slate-700/50 rounded-2xl border border-slate-100 dark:border-slate-600 italic relative">
                  <div className="absolute -top-3 left-6 px-3 py-1 bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-600 rounded-lg text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest">
                    {t("messages.originalMessage", "Ursprüngliche Nachricht")}
                  </div>
                  <p className="text-sm text-slate-500 dark:text-slate-400 font-medium leading-relaxed line-clamp-3">
                    {selectedMessageForReply.content}
                  </p>
                </div>
              )}

              {/* Text Area */}
              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-[#0f7699] opacity-70 ml-1">
                  {t("messages.yourReply", "Deine Antwort")}
                </label>
                <textarea
                  rows={6}
                  placeholder={t("messages.writeReplyPlaceholder", "Schreiben Sie Ihre Antwort hier...")}
                  value={replyContent}
                  onChange={(e) => setReplyContent(e.target.value)}
                  disabled={isSendingReply}
                  className="w-full p-4 sm:p-6 bg-slate-50 dark:bg-slate-700 border-2 border-transparent focus:border-[#0f7699] dark:focus:border-[#4fc3f7] focus:bg-white dark:focus:bg-slate-600 rounded-2xl sm:rounded-[1.5rem] text-slate-700 dark:text-white font-bold placeholder:text-slate-400 dark:placeholder:text-slate-500 transition-all outline-none resize-none"
                />
              </div>

              {/* Modal Actions */}
              <div className="flex flex-col sm:flex-row justify-end gap-3 sm:gap-4">
                <button
                  onClick={handleCloseReplyDialog}
                  disabled={isSendingReply}
                  className="px-5 sm:px-8 h-11 sm:h-14 bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-300 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-slate-200 dark:hover:bg-slate-600 transition-all"
                >
                  {t("messages.cancel", "Abbrechen")}
                </button>
                <button
                  onClick={handleSendReply}
                  disabled={!replyContent.trim() || isSendingReply}
                  className="px-6 sm:px-10 h-11 sm:h-14 bg-[#0f7699] text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl shadow-blue-900/20 hover:bg-[#0d6482] transition-all flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSendingReply ? (
                    <div className="w-5 h-5 border-2 border-white/50 border-t-white rounded-full animate-spin"></div>
                  ) : (
                    <Send size={18} />
                  )}
                  <span>{isSendingReply ? t("messages.sending", "Senden...") : t("messages.sendReply", "Senden")}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Confirmation Modal for Closing Ticket */}
      {confirmCloseOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-300"
            onClick={() => setConfirmCloseOpen(false)}
          />

          {/* Modal Content */}
          <div className="relative w-full max-w-md bg-white dark:bg-slate-800 rounded-2xl sm:rounded-[2.5rem] p-5 sm:p-8 shadow-2xl shadow-slate-900/20 border border-white/20 dark:border-slate-700 animate-in zoom-in-95 duration-300">
            <div className="flex flex-col items-center text-center space-y-6">
              {/* Icon */}
              <div className="w-16 h-16 bg-amber-50 dark:bg-amber-900/20 rounded-full flex items-center justify-center">
                <AlertCircle className="text-amber-500 dark:text-amber-400" size={32} />
              </div>

              {/* Text */}
              <div className="space-y-2">
                <h3 className="text-xl font-black text-slate-900 dark:text-white">
                  {t("messages.closeTicketQuestion", "Ticket schließen?")}
                </h3>
                <p className="text-sm text-slate-500 dark:text-slate-400 font-medium leading-relaxed">
                  {t("messages.confirmCloseDesc", "Möchten Sie dieses Ticket wirklich schließen? Die Konversation wird archiviert und es können keine weiteren Antworten gesendet werden.")}
                </p>
              </div>

              {/* Actions */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full pt-4">
                <button
                  onClick={() => setConfirmCloseOpen(false)}
                  className="h-12 rounded-2xl border-2 border-slate-100 dark:border-slate-600 text-slate-500 dark:text-slate-300 font-bold text-xs uppercase tracking-widest hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                >
                  {t("messages.cancel", "Abbrechen")}
                </button>
                <button
                  onClick={() => {
                    if (ticketToClose) {
                      setCurrentlyClosingIds(prev => new Set(prev).add(ticketToClose));
                      closeConversation(ticketToClose);
                    }
                    setConfirmCloseOpen(false);
                    setTicketToClose(null);
                  }}
                  className="h-12 rounded-2xl bg-slate-900 text-white font-bold text-xs uppercase tracking-widest hover:bg-slate-800 transition-colors shadow-lg shadow-slate-900/10"
                >
                  {t("messages.close", "Schließen")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
